import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { CurrencyExchangeRateService } from '@app/v2/services/currency-exchange-rate/currency-exchange-rate.service';
import { StipendRequestService } from '@app/v2/services/stipend-request/stipend-request.service';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import {
    Chip,
    MdsChipComponent,
    MdsDatepickerComponent,
    MdsDividerComponent,
    MdsDropdownComponent,
    MdsTextFieldComponent,
    MdsTextareaComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { Currency, CurrencyViewModel } from '@models/currency';
import { DateFormat } from '@models/date-format';
import { PaymentRequestStatus, StipendRequest } from '@models/request';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import { RequestMerchantStateService } from '@services/state-management/request-merchant-state.service';
import { SiteStateService } from '@services/state-management/site-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { UserStateService } from '@services/state-management/user-state.service';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { maxLengthValidator, notWhitespaceValidator } from '@utility/utility.validators';
import moment from 'moment';
import {
    BehaviorSubject,
    combineLatest,
    debounceTime,
    filter,
    map,
    merge,
    of,
    shareReplay,
    skip,
    switchMap,
    tap,
} from 'rxjs';
import {
    PaymentRequestDeductionEditComponent,
    PaymentRequestDeductionFormGroup,
} from '../../payment-request-deduction/payment-request-deduction-edit/payment-request-deduction-edit.component';
import {
    computeAuthorizedAmountPatientsCurrencyChip,
    computeAuthorizedAmountUSDChip,
} from '../../reimbursement-request/expense/expense-chips.util';

@Component({
    selector: 'medpace-stipend-request-edit',
    imports: [
        MdsDropdownComponent,
        AsyncPipe,
        MdsTextFieldComponent,
        MdsDatepickerComponent,
        PaymentRequestDeductionEditComponent,
        dateFormatPipe,
        UpperCasePipe,
        MdsDividerComponent,
        MdsChipComponent,
        MdsTextareaComponent,
    ],
    templateUrl: './stipend-request-edit.component.html',
    styleUrl: './stipend-request-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StipendRequestEditComponent {
    formGrp = input.required<StipendRequestFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    request = input<StipendRequest>();
    requestMaybe$ = toObservable(this.request);
    requestNotNull$ = this.requestMaybe$.pipe(filter(Boolean));

    userStateService = inject(UserStateService);
    userService = inject(UserServiceV2);
    currencyStateService = inject(CurrencyStateService);
    studyStateService = inject(StudyStateService);
    siteStateService = inject(SiteStateService);
    patientStateService = inject(PatientStateService);
    requestService = inject(StipendRequestService);

    merchantStateService = inject(RequestMerchantStateService);
    merchantTypes$ = this.merchantStateService.getPaymentRequestMerchants();
    elevatedAccess$ = this.userService.permissions.request.access.elevated$;
    currencyOptions$ = this.currencyStateService.getCurrenciesAsMdsOptions();
    private readonly currencyExchangeRateService = inject(CurrencyExchangeRateService);
    patient$ = this.patientStateService.getPatient();
    patient = toSignal(this.patient$);

    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );

    currency$ = this.formGrp$.pipe(switchMap((fg) => fg.controls.currency.valueChangesReplay));
    private readonly currencySignal = toSignal(this.currency$);
    private readonly patientPreferredCurrency$ = toObservable(this.patientPreferredCurrency);

    isCurrencyUSD = computed(() => {
        const currency = this.currencySignal();
        if (!currency) return null;
        return currency.isocode.toLowerCase() === 'usd';
    });
    requestedLabel = computed(() => {
        const currency = this.currencySignal();
        return currency ? `Requested (${currency.isocode})` : 'Requested';
    });
    authorizedLabel = computed(() => {
        const currency = this.currencySignal();
        return currency ? `Authorized (${currency.isocode})` : 'Authorized';
    });

    private readonly chipAmounts$ = combineLatest({
        request: this.requestMaybe$,
        formGrp: this.formGrp$,
    }).pipe(
        switchMap(({ request, formGrp }) => {
            const initial$ = of({
                patientsCurrency: request?.authorizedAmountPatientsCurrency ?? null,
                usd: request?.authorizedAmountUsd ?? null,
                exchangeRateDate: request?.exchangeRateDate ?? null,
            });

            const hasStoredValues =
                request?.authorizedAmountPatientsCurrency != null || request?.authorizedAmountUsd != null;

            const visitDate = this.request()?.visitStartDate ?? null;

            const recalculate$ = combineLatest({
                _trigger: merge(
                    formGrp.controls.authorizedAmountInLocalCurrency.valueChanges,
                    formGrp.controls.transactionDate.valueChanges,
                    this.currency$.pipe(skip(1))
                ),
                authorizedStr: formGrp.controls.authorizedAmountInLocalCurrency.valueChangesReplay,
                date: of(visitDate),
                currency: this.currency$,
                patientCurrency: this.patientPreferredCurrency$,
                isInProcessOrHigherAndHasElevatedAccess: this.isInProcessOrHigherAndHasElevatedAccess$,
            }).pipe(
                debounceTime(300),
                skip(hasStoredValues ? 1 : 0),
                switchMap(
                    ({ authorizedStr, date, currency, patientCurrency, isInProcessOrHigherAndHasElevatedAccess }) => {
                        const authorized = authorizedStr ? parseFloat(authorizedStr) : null;
                        if (
                            !authorized ||
                            isNaN(authorized) ||
                            !date ||
                            !currency ||
                            !isInProcessOrHigherAndHasElevatedAccess
                        ) {
                            return of({
                                patientsCurrency: null as number | null,
                                usd: null as number | null,
                                exchangeRateDate: null as string | null,
                            });
                        }

                        return this.currencyExchangeRateService.convertAmount(
                            authorized,
                            currency.isocode.toUpperCase(),
                            date,
                            patientCurrency
                        );
                    }
                )
            );

            return merge(initial$, recalculate$);
        })
    );

    private readonly chipAmountsSignal = toSignal(this.chipAmounts$);

    private readonly chipSource = computed(() => {
        const currency = this.currencySignal();
        if (!currency) return null;
        const amounts = this.chipAmountsSignal();
        return {
            currency: currency.isocode,
            authorizedAmountPatientsCurrency: amounts?.patientsCurrency,
            authorizedAmountUsd: amounts?.usd,
        };
    });

    authorizedAmountPatientsCurrencyChip = computed((): Chip[] | null =>
        computeAuthorizedAmountPatientsCurrencyChip(this.chipSource(), this.patientPreferredCurrency())
    );

    authorizedAmountUSDChip = computed((): Chip[] | null => computeAuthorizedAmountUSDChip(this.chipSource()));

    dateRequested$ = this.requestMaybe$.pipe(
        map((request) => request?.createdTimestamp?.toDate() ?? moment().toDate())
    );
    dateSubmitted$ = this.requestMaybe$.pipe(map((request) => request?.submittedTimestamp?.toDate() ?? null));
    dateOnly = DateFormat.dateOnly;

    push(request: StipendRequest): StipendRequest {
        const amounts = this.chipAmountsSignal();
        const currency = this.currencySignal()?.isocode.toUpperCase();
        const patientCurrency = this.patientPreferredCurrency();

        const value = this.formGrp().getRawValue();
        request.amount = !value.requestedAmountInLocalCurrency ? null : +value.requestedAmountInLocalCurrency;
        request.authorizedAmount = !value.authorizedAmountInLocalCurrency
            ? null
            : +value.authorizedAmountInLocalCurrency;
        request.currency = value.currency.isocode;
        request.transactionDate = value.transactionDate
            ? moment(value.transactionDate).format('YYYY-MM-DDTHH:mm:ss')
            : null;

        request.merchant = value.merchant;
        request.notes = value.notes;

        request.authorizedAmountUsd =
            currency === 'USD'
                ? value.authorizedAmountInLocalCurrency == null || value.authorizedAmountInLocalCurrency === ''
                    ? null
                    : +value.authorizedAmountInLocalCurrency
                : (amounts?.usd ?? null);

        request.authorizedAmountPatientsCurrency =
            currency === 'USD' && patientCurrency == currency
                ? value.authorizedAmountInLocalCurrency == null || value.authorizedAmountInLocalCurrency === ''
                    ? null
                    : +value.authorizedAmountInLocalCurrency
                : (amounts?.patientsCurrency ?? null);

        request.exchangeRateDate = amounts?.exchangeRateDate ?? null;

        if (this.isVoidedSubject.getValue()) {
            // if ias voided
            request.deducted = value.deduction.amount;
            request.deductedUsd = value.deduction.amountConverted;
            request.deductedCurrency = value.deduction.currency?.isocode;
        }
        return request;
    }
    isVoidedSubject = new BehaviorSubject<boolean>(null);
    isVoided$ = this.requestMaybe$.pipe(
        map((request) => {
            if (!request) return null;
            return request.status?.statusId === PaymentRequestStatus.Voided;
        }),
        tap((isVoided) => {
            this.isVoidedSubject.next(isVoided);
        })
    );
    switchVoided$ = combineLatest({
        formGrp: this.formGrp$,
        isVoided: this.isVoided$,
    })
        .pipe(
            tap(({ formGrp, isVoided }) => {
                if (isVoided) {
                    formGrp.controls.deduction.enable({ emitEvent: true, force: true });
                } else {
                    formGrp.controls.deduction.disable({ emitEvent: true, persistent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();
    patchVoided$ = combineLatest({
        request: this.requestNotNull$,
        currencies: this.currencyStateService.getCurrencies(),
        isVoided: this.isVoided$,
    })
        .pipe(
            filter(({ isVoided }) => isVoided === true),
            tap(({ currencies, request }) => {
                const deductedCurrency = currencies?.find((currency) => currency.isocode === request.deductedCurrency);
                this.formGrp().patchValue({
                    deduction: {
                        amount: request.deducted,
                        amountConverted: request.deductedUsd,
                        currency: deductedCurrency,
                    },
                });
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    patch$ = combineLatest({
        request: this.requestNotNull$,
        elevatedAccess: this.elevatedAccess$,
        currencies: this.currencyStateService.getCurrencies(),
    })
        .pipe(
            tap(({ elevatedAccess, request, currencies }) => {
                this.formGrp().patchValue(
                    {
                        requestedAmountInLocalCurrency: request.amount?.toFixed(2) ?? null,
                        currency:
                            currencies.find(
                                (currency) => currency.isocode.toLowerCase() === request.currency.toLowerCase()
                            ) ?? null,
                        merchant: request.merchant,
                        transactionDate: request.transactionDate ? new Date(request.transactionDate) : null,
                        notes: request.notes ?? null,
                    },
                    { emitEvent: true }
                );

                if (elevatedAccess) {
                    let authorizedAmount = request.authorizedAmount;
                    if (authorizedAmount === null || authorizedAmount === undefined) authorizedAmount = request.amount; // if authorized was not yet filled, initialize it with requestedAmount, for convenience only
                    this.formGrp().patchValue(
                        {
                            authorizedAmountInLocalCurrency: authorizedAmount?.toFixed(2) ?? null,
                            authorizedAmountInUSD: request.authorizedAmountUsd?.toFixed(2) ?? null,
                        },
                        { emitEvent: true }
                    );
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    initializePreferredCurrencyEffect$ = combineLatest({
        request: this.requestMaybe$,
        patient: this.patientStateService.getPatient(),
        currencies: this.currencyStateService.getCurrencies(),
        currencyUSD: this.currencyStateService.getCurrencyUSD(),
        formGrp: this.formGrp$,
    })
        .pipe(
            filter(({ request }) => !request), // expense hasnt emitted yet
            tap(({ currencies, currencyUSD, patient, formGrp }) => {
                let currency: CurrencyViewModel | null = null;
                if (patient?.preferredCurrency) {
                    currency = currencies.find((currency) => currency.isocode === patient.preferredCurrency);
                }
                if (!currency) currency = currencyUSD;

                if (currency) formGrp.controls.currency.setValue(currency);
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    isInProcessOrHigherAndHasElevatedAccess$ = combineLatest({
        elevatedAccess: this.elevatedAccess$,
        isInProcessOrHigher: this.requestService.isInProcessOrHigher$,
    }).pipe(
        map(({ elevatedAccess, isInProcessOrHigher }) => {
            return elevatedAccess && isInProcessOrHigher;
        }),
        shareReplay(1)
    );
    switchAuthorized$ = combineLatest({
        isInProcessOrHigherAndHasElevatedAccess: this.isInProcessOrHigherAndHasElevatedAccess$,
        formGrp: this.formGrp$,
    })
        .pipe(
            tap(({ isInProcessOrHigherAndHasElevatedAccess, formGrp }) => {
                if (isInProcessOrHigherAndHasElevatedAccess) {
                    formGrp.controls.authorizedAmountInLocalCurrency.enable({ force: true });
                    formGrp.controls.authorizedAmountInUSD.enable({ force: true });
                } else {
                    formGrp.controls.authorizedAmountInLocalCurrency.disable({ persistent: true });
                    formGrp.controls.authorizedAmountInUSD.disable({ persistent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();
}
export class StipendRequestFormGroup extends PersistentFormGroup<{
    currency: PersistentFormControl<Currency>;
    requestedAmountInLocalCurrency: PersistentFormControl<string>;
    authorizedAmountInLocalCurrency: PersistentFormControl<string>;
    authorizedAmountInUSD: PersistentFormControl<string>;
    transactionDate: PersistentFormControl<Date>;
    merchant: PersistentFormControl<number>;
    deduction: PaymentRequestDeductionFormGroup;
    notes: PersistentFormControl<string | null>;
}> {
    constructor() {
        super({
            currency: new PersistentFormControl<Currency>(null, [Validators.required]),
            requestedAmountInLocalCurrency: new PersistentFormControl<string>(null, [
                Validators.required,
                notWhitespaceValidator(),
                Validators.min(-1e6),
                Validators.max(1e6),
            ]),
            authorizedAmountInLocalCurrency: new PersistentFormControl<string>(null, [
                Validators.required,
                notWhitespaceValidator(),
                Validators.min(-1e6),
                Validators.max(1e6),
            ]),
            authorizedAmountInUSD: new PersistentFormControl<string>(null, [
                notWhitespaceValidator(),
                Validators.min(-1e6),
                Validators.max(1e6),
            ]),
            transactionDate: new PersistentFormControl<Date>(null),
            merchant: new PersistentFormControl<number>(0),
            deduction: new PaymentRequestDeductionFormGroup(),
            notes: new PersistentFormControl<string | null>(null, [maxLengthValidator(500), notWhitespaceValidator()]),
        });
    }
}
